import React, { useEffect, useState } from "react";
import { useParams, Link, useSearchParams } from "react-router-dom";
import { Phone, Mail, MessageSquare, Download, CheckCircle2, Globe } from "lucide-react";
import { portalApi } from "./api";
import { BRAND } from "@/data/brand";

export default function PublicCard() {
  const { slug } = useParams();
  const [searchParams] = useSearchParams();
  const isAdminPreview = searchParams.get("adminPreview") === "1";
  const [card, setCard] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetcher = isAdminPreview ? portalApi.adminPreviewCard(slug) : portalApi.publicCard(slug);
    fetcher
      .then(setCard)
      .catch((e) => setError(e?.response?.status === 404 ? "Card not found" : "Unable to load"));
  }, [slug, isAdminPreview]);

  if (error) {
    return (
      <main className="min-h-screen bg-midnight-deeper flex items-center justify-center px-6 text-center" data-testid="public-card-404">
        <div>
          <div className="font-mono text-[0.62rem] text-gold tracking-[0.32em] uppercase mb-4">404</div>
          <h1 className="font-display-tight text-white text-3xl mb-4">Card not found</h1>
          <p className="text-ag-text-secondary text-sm mb-8">This digital card is not available.</p>
          <Link to="/" className="btn-gold inline-flex items-center gap-2 !py-2.5 !px-5">AG Public Adjusting</Link>
        </div>
      </main>
    );
  }
  if (!card) {
    return <main className="min-h-screen bg-midnight-deeper flex items-center justify-center text-ag-text-muted font-mono text-xs tracking-[0.3em]">LOADING…</main>;
  }

  const fullName = `${card.first_name} ${card.last_name}`.trim();
  const vcardUrl = portalApi.vcardUrl(slug);

  return (
    <main data-testid="public-card" className="min-h-screen bg-midnight-deeper px-5 py-8 sm:py-12">
      <div className="max-w-md mx-auto">
        {isAdminPreview && card._preview_meta && (
          <div className="mb-4 border border-gold/40 bg-gold/5 text-gold text-[0.6rem] font-mono tracking-[0.24em] uppercase px-3 py-2 text-center" data-testid="admin-preview-banner">
            Admin Preview · {card._preview_meta.published ? "Published" : "Draft"}{card._preview_meta.suspended ? " · Suspended" : ""}
          </div>
        )}
        {/* Header */}
        <Link to="/" className="flex items-center justify-center gap-3 mb-10">
          <img src={BRAND.logoWhite} alt="AG Public Adjusting" className="h-10 w-auto object-contain" />
          <div className="leading-tight">
            <div className="font-mono text-[0.55rem] mt-0.5 text-gold/80 tracking-[0.28em]">LICENSED · FL</div>
          </div>
        </Link>

        {/* Card */}
        <div className="glass-strong p-8 text-center space-y-6" style={{ boxShadow: "inset 0 1px 0 rgba(198,168,107,0.15), 0 40px 80px -20px rgba(0,119,255,0.25)" }}>
          {card.photo_url ? (
            <img src={card.photo_url} alt={fullName} className="w-32 h-32 rounded-full object-cover mx-auto border border-gold/40" style={{ boxShadow: "0 0 30px rgba(198,168,107,0.25)" }} />
          ) : (
            <div className="w-32 h-32 rounded-full mx-auto border border-white/10 bg-white/5" />
          )}

          <div>
            <h1 className="font-display-tight text-white text-3xl sm:text-4xl tracking-[-0.03em] leading-[1] font-light" data-testid="public-card-name">{fullName}</h1>
            <div className="mt-2 text-ag-text-secondary text-sm">{card.professional_title}</div>
            {card.license_number && <div className="mt-4 font-mono text-[0.62rem] text-gold/80 tracking-[0.28em] uppercase">License # {card.license_number}</div>}
          </div>

          {card.license_verified ? (
            <div className="inline-flex items-center gap-2 px-3 py-2 border border-emerald-400/40 text-emerald-300 bg-emerald-400/10" data-testid="public-card-verified">
              <CheckCircle2 className="w-3.5 h-3.5" />
              <span className="font-mono text-[0.62rem] tracking-[0.24em] uppercase">License Verified</span>
            </div>
          ) : (
            <div className="inline-flex items-center gap-2 px-3 py-2 border border-white/10 text-white/40" data-testid="public-card-unverified">
              <span className="font-mono text-[0.6rem] tracking-[0.24em] uppercase">AG Public Adjusting</span>
            </div>
          )}

          {/* CTA buttons */}
          <div className="grid grid-cols-2 gap-3 pt-2">
            <a href={`tel:${card.phone}`} className="btn-gold inline-flex items-center justify-center gap-2 !py-3 !text-[0.68rem]" data-testid="public-card-call">
              <Phone className="w-3.5 h-3.5" /> Call
            </a>
            <a href={`sms:${card.phone}`} className="btn-ghost-blue inline-flex items-center justify-center gap-2 !py-3 !text-[0.68rem]" data-testid="public-card-text">
              <MessageSquare className="w-3.5 h-3.5" /> Text
            </a>
            <a href={`mailto:${card.professional_email}`} className="btn-ghost-blue inline-flex items-center justify-center gap-2 !py-3 !text-[0.68rem]" data-testid="public-card-email">
              <Mail className="w-3.5 h-3.5" /> Email
            </a>
            <a href={vcardUrl} className="btn-ghost-blue inline-flex items-center justify-center gap-2 !py-3 !text-[0.68rem]" data-testid="public-card-vcard">
              <Download className="w-3.5 h-3.5" /> Save
            </a>
          </div>
        </div>

        {/* Additional info */}
        {(card.languages?.length || card.service_areas?.length || card.bio) && (
          <div className="mt-6 glass p-6 space-y-5">
            {card.bio && (
              <div>
                <div className="font-mono text-[0.55rem] text-gold tracking-[0.28em] uppercase mb-2">About</div>
                <p className="text-ag-text-secondary text-sm leading-[1.65] font-light">{card.bio}</p>
              </div>
            )}
            {card.languages?.length > 0 && (
              <div>
                <div className="font-mono text-[0.55rem] text-gold tracking-[0.28em] uppercase mb-2">Languages</div>
                <p className="text-white text-sm font-light">{card.languages.join(" · ")}</p>
              </div>
            )}
            {card.service_areas?.length > 0 && (
              <div>
                <div className="font-mono text-[0.55rem] text-gold tracking-[0.28em] uppercase mb-2">Service Area</div>
                <p className="text-white text-sm font-light">{card.service_areas.join(" · ")}</p>
              </div>
            )}
          </div>
        )}

        {/* Footer */}
        <div className="mt-10 pt-8 border-t border-white/8 text-center space-y-4">
          <Link to="/" className="inline-flex items-center gap-2 text-xs font-mono text-white/70 hover:text-gold tracking-[0.22em] uppercase">
            <Globe className="w-3 h-3" /> agpublicadjusting.com
          </Link>
          <div className="flex items-center justify-center gap-4 text-xs font-mono text-ag-text-muted tracking-[0.22em] uppercase">
            <a href={BRAND.phoneHref} className="hover:text-gold transition-colors">{BRAND.phoneDisplay}</a>
            <span className="w-px h-3 bg-white/10" />
            <a href={BRAND.emailHref} className="hover:text-gold transition-colors">Contact</a>
          </div>
          <p className="text-[0.65rem] text-ag-text-muted font-light">© {new Date().getFullYear()} AG Public Adjusting · {BRAND.licenseNumber}</p>
        </div>
      </div>
    </main>
  );
}
